import { range, isEmpty } from 'lodash'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { CurrentSort } from '@e3dc-react/shell/Elements/Table'
import { useTypedSelector } from 'src/State/RootReducer'
import { ChargingSessionModel } from '../models/charging-sessions.model'
import { ChargingSessionSearchResponseDto } from '../api/dto/charging-sessions.dto'
import { chargingSessionTransformer } from '../api/transformers/charging-session.transformer'
import { ChargingSessionSearchRequestDto } from '../api/dto/charging-session-search-request.dto'
import { ChargingSessionStatus, MIDMeterFilterValue } from '../types'
import { NOT_AUTHENTICATED_FLAG } from '../models/not-authenticated-flag'
import { MOCK_CHARGING_SESSIONS } from 'src/Libs/e-mobility/guided-tour'
import { isWithinInterval } from '@e3dc-react/shell/Libs/date'
import { usePortalQuery } from 'src/Libs/api'
// import { SortableColumnsDto } from '../api/dto/charging-session-search-request.dto'

/**
 * At the moment, HagerBridge backend does not fully support sorting and filtering by all fields when using backend pagination.
 * For this reason, we fetch ALL sessions at once and apply filtering and sorting in frontend. We do this by fetching multiple
 * pages of data and merge them into a single list to be consumed by FE.
 *
 * Multiple fetches are not ideal, but are a temporary implementation due to page size limit of 200 that was introduced in HagerBridge
 * GET /sessions endpoint.
 */
const MAX_PAGE_SIZE = 100

interface UseChargingSessionsHook {
  data: ChargingSessionModel[] | undefined
  unfilteredData: ChargingSessionModel[] | undefined
  isLoading: boolean
  isError: boolean
}

export type SortableColumns = Pick<ChargingSessionModel, 'status' | 'startDateTime' | 'consumptionKWh' | 'totalCost' | 'authentication'>

interface UseChargingSessionsParams {
  installationId: number | undefined
  startDate?: string
  endDate?: string
  wallboxId?: string[]
  ocpiTokenIds?: string[]
  status?: ChargingSessionStatus[]
  midMeter: string[]
  sort?: CurrentSort<SortableColumns>
  isInstallationDeleted?: boolean
}

// Uncomment once BE fully supports pagination, sorting and filtering over all columns
// const sortMapper: Record<keyof SortableColumns, keyof SortableColumnsDto> = {
//   status: 'status',
//   startDateTime: 'startDateTime',
//   consumptionKWh: 'kwh',
//   totalCost: 'totalCost',
//   authentication: 'cdrToken',
// }

/**
 * Used to fetch charging sessions of a single installation. At the moment, ALL sessions are fetched in one request.
 *
 * @param installationId - string
 * @param wallboxIds - string[]
 * @param ocpiTokenIds - string[]
 * @param midMeter - string[]
 * @param dateFrom - Date
 * @param dateTo - Date
 * @param sort - array of strings
 * @returns data as ChargingSessionResponseModel or undefined, isError as boolean, isLoading as boolean
 */

export const useChargingSessions = (params: UseChargingSessionsParams): UseChargingSessionsHook => {
  const authFetch = useAuthFetchWithResult()
  const eMobilityTourRunning = useTypedSelector(state => state.eMobility.tour.isRunning)

  const fetchChargingSessions = async (
    params: ChargingSessionSearchRequestDto & { installationId: number | undefined }
  ): Promise<ChargingSessionSearchResponseDto> => {
    const payload: ChargingSessionSearchRequestDto = {
      page: params.page,
      size: params.size,
      // Enable fields once BE fully suports pagination and sorting over all columns
      //
      // wallboxIds: params.wallboxIds || [],
      // tokenIds: params.rfid || [],
      // dateFrom: params.dateFrom ? moment(params.dateFrom).startOf('day').utc().format(`YYYY-MM-DDTHH:mm:ss`) : undefined,
      // dateTo: params.dateTo ? moment(params.dateTo).endOf('day').utc().format(`YYYY-MM-DDTHH:mm:ss`) : undefined,
      // sort: params.sort ? [`${sortMapper[params.sort.name]},${params.sort.direction === 'asc' ? 'ASC' : 'DESC'}`] : [],
    }

    return authFetch<ChargingSessionSearchResponseDto>({
      path: `sessions/installation/${params.installationId}`,
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'GET',
      payload,
    })
  }

  const { data, isError, isLoading } = usePortalQuery({
    queryFn: async () => {
      if (eMobilityTourRunning) {
        return MOCK_CHARGING_SESSIONS
      }

      const firstPage = await fetchChargingSessions({
        installationId: params.installationId,
        page: 0,
        size: MAX_PAGE_SIZE,
        sort: undefined,
      })

      const { page, totalPages: totalPagesLegacy } = firstPage
      const totalPages = page
        ? page.totalPages
        : totalPagesLegacy /** check charging-sessions.dto.ts for explanation why we're reading the same property from two separate fields */

      const remainingPages = await Promise.all(
        range(1, totalPages).map(page => {
          return fetchChargingSessions({
            installationId: params.installationId,
            page,
            size: MAX_PAGE_SIZE,
            sort: undefined,
          })
        })
      )

      return firstPage.content.concat(...remainingPages.map(page => page.content))
    },
    initialData: eMobilityTourRunning ? MOCK_CHARGING_SESSIONS : undefined,
    queryKey: ['chargingSessions', params.installationId],
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: Boolean(params.installationId) && !params.isInstallationDeleted,
    // keepPreviousData: true, // uncomment when BE pagination is enabled, otherwise stale data is shown on page change
    // cacheTime: 0, // uncomment when BE pagination is enabled, otherwise stale data is shown on page change
  })

  const result = data ? data.map(chargingSessionTransformer) : undefined

  // Remove this filtering and immediately return "result" above once BE fully supports pagination, filtering
  // and sorting over all required columns. Until then, do filtering in frontend.
  const filteredResult = result
    ? result
        .filter(session => {
          if (isEmpty(params.wallboxId)) {
            return true
          }

          return params.wallboxId?.includes(session.wallbox.id)
        })
        .filter(session => {
          if (isEmpty(params.status)) {
            return true
          }

          return params.status?.includes(session.status)
        })
        .filter(session => {
          if (isEmpty(params.ocpiTokenIds)) {
            return true
          }
          const isAuthorized = session.authentication.authenticationMethod === 'OCPP' || session.authentication.authenticationMethod === 'RFID'

          if (!isAuthorized) {
            if (params.ocpiTokenIds?.includes(NOT_AUTHENTICATED_FLAG)) {
              return true
            }

            return false
          }

          /**
           * The filtering logic seen below is not trivial, e.g. "find unique ociTokenId strings in the session array":
           * When an RFID card is presented to the EasyConnect wallbox, only the first 8 characters of the RFID are read and sent to backend.
           * However, this same card can be presented to a newer wallbox such as MultiConnect, for which all 32 characters of the RFID are read
           * and then stored in the database.
           *
           * It is decided to show the 32-character long version of the RFID in the filter dropdown whenever possible, and for this reason the
           * filtering by charging card must be adjusted so that the selected charging card matches "session.authentication.ocpiTokenid" field regardless if the session
           * was started on an EasyConnect wallbox(8 characters) or some newer wallbox such as MultiConnect(32 characters)
           */
          return params.ocpiTokenIds?.includes(session.cdrToken.uid)
        })
        .filter(session => {
          if (!params.startDate && !params.endDate) {
            return true
          }

          const isInRange = isWithinInterval(new Date(session.startDateTime), {
            start: new Date(params.startDate ?? 0),
            end: new Date(params.endDate ?? 0),
          })

          return isInRange
        })
        .filter(session => {
          if (isEmpty(params.midMeter)) {
            return true
          }

          if (
            params.midMeter.some(meter => meter === MIDMeterFilterValue.withMIDMeter) &&
            params.midMeter.some(meter => meter === MIDMeterFilterValue.withoutMIDMeter)
          ) {
            return true
          }

          if (params.midMeter.some(meter => meter === MIDMeterFilterValue.withoutMIDMeter)) {
            return !session.midInfo.midMeterConnected
          }

          return session.midInfo.midMeterConnected
        })
    : undefined

  return {
    data: filteredResult,
    unfilteredData: result,
    isError: isError,
    isLoading: isLoading,
  }
}
